import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grape, HelpCircle, FileText, Trash2, UploadCloud, CheckCircle2, Loader2, ArrowRight, AlertCircle, Calendar } from 'lucide-react';
import { Button } from './ui/Button';
export function ViewStatementsPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([{
    id: 1,
    name: 'Chase_Business_Checking_Oct2025.pdf',
    size: '2.4 MB',
    date: 'Oct 24, 2025',
    status: 'ready',
    type: 'bank'
  }, {
    id: 2,
    name: 'Amex_Platinum_Q3_2025.csv',
    size: '845 KB',
    date: 'Oct 24, 2025',
    status: 'processing',
    type: 'upload'
  }, {
    id: 3,
    name: 'QuickBooks_General_Ledger.xlsx',
    size: '1.2 MB',
    date: 'Oct 24, 2025',
    status: 'ready',
    type: 'integration'
  }]);
  const handleDelete = (id: number) => {
    setFiles(files.filter(f => f.id !== id));
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-877">
      {/* Simplified Top Bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-878">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center" data-id="element-879">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/connect')} data-id="element-880">

            <div className="bg-violet-100 p-1.5 rounded-lg mr-2" data-id="element-881">
              <Grape className="h-5 w-5 text-violet-600" data-id="element-882" />
            </div>
            <span className="text-lg font-bold text-gray-900 tracking-tight" data-id="element-883">
              Berry Finance
            </span>
          </div>
          <button className="text-sm font-medium text-gray-500 hover:text-gray-900 flex items-center gap-1.5 transition-colors" data-id="element-884">
            <HelpCircle className="h-4 w-4" data-id="element-885" />
            Need help?
          </button>
        </div>
      </header>

      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8" data-id="element-886">
        <div className="max-w-3xl mx-auto" data-id="element-887">
          {/* Progress Stepper */}
          <div className="mb-12" data-id="element-888">
            <div className="flex items-center justify-between relative" data-id="element-889">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-gray-200 -z-10" data-id="element-890"></div>

              {/* Step 1: Completed */}
              <div className="flex flex-col items-center bg-gray-50 px-2 cursor-pointer" onClick={() => navigate('/connect')} data-id="element-891">

                <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold text-sm mb-2 ring-4 ring-gray-50" data-id="element-892">
                  <CheckCircle2 className="h-5 w-5" data-id="element-893" />
                </div>
                <span className="text-xs font-bold text-violet-600 uppercase tracking-wide" data-id="element-894">
                  Connect
                </span>
              </div>

              {/* Step 2: Active */}
              <div className="flex flex-col items-center bg-gray-50 px-2" data-id="element-895">
                <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold text-sm mb-2 ring-4 ring-gray-50" data-id="element-896">
                  2
                </div>
                <span className="text-xs font-bold text-violet-600 uppercase tracking-wide" data-id="element-897">
                  Upload
                </span>
              </div>

              {/* Step 3: Upcoming */}
              <div className="flex flex-col items-center bg-gray-50 px-2" data-id="element-898">
                <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center font-bold text-sm mb-2 ring-4 ring-gray-50" data-id="element-899">
                  3
                </div>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wide" data-id="element-900">
                  Reconcile
                </span>
              </div>
            </div>
          </div>

          {/* Page Header */}
          <div className="text-center mb-10" data-id="element-901">
            <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-902">
              Review uploaded statements
            </h1>
            <p className="text-lg text-gray-600 max-w-xl mx-auto" data-id="element-903">
              We've pulled the following data from your connected accounts.
              Review and add any missing statements.
            </p>
          </div>

          {/* Upload Area */}
          <div className="mb-8" data-id="element-904">
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-violet-400 hover:bg-violet-50/30 transition-colors cursor-pointer bg-white" data-id="element-905">
              <div className="w-12 h-12 bg-violet-100 text-violet-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-906">
                <UploadCloud className="h-6 w-6" data-id="element-907" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-908">
                Click to upload or drag and drop
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-909">
                PDF, CSV, OFX, or Excel files (max 25MB)
              </p>
              <Button variant="outline" size="sm" data-id="element-910">
                Select Files
              </Button>
            </div>
          </div>

          {/* Files List */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8" data-id="element-911">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50" data-id="element-912">
              <h3 className="font-semibold text-gray-900" data-id="element-913">
                Uploaded Files ({files.length})
              </h3>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-914">
                Status
              </span>
            </div>

            <div className="divide-y divide-gray-100" data-id="element-915">
              {files.map(file => <div key={file.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors group" data-id="element-916">

                  <div className="flex items-center gap-4" data-id="element-917">
                    <div className={`p-2 rounded-lg ${file.type === 'bank' ? 'bg-blue-100 text-blue-600' : file.type === 'integration' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-918">

                      <FileText className="h-5 w-5" data-id="element-919" />
                    </div>
                    <div data-id="element-920">
                      <p className="font-medium text-gray-900" data-id="element-921">{file.name}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5" data-id="element-922">
                        <span data-id="element-923">{file.size}</span>
                        <span data-id="element-924">•</span>
                        <span className="flex items-center gap-1" data-id="element-925">
                          <Calendar className="h-3 w-3" data-id="element-926" /> {file.date}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6" data-id="element-927">
                    {file.status === 'processing' ? <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1 rounded-full text-xs font-medium" data-id="element-928">
                        <Loader2 className="h-3 w-3 animate-spin" data-id="element-929" />
                        Processing...
                      </div> : <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1 rounded-full text-xs font-medium" data-id="element-930">
                        <CheckCircle2 className="h-3 w-3" data-id="element-931" />
                        Ready
                      </div>}

                    <button onClick={() => handleDelete(file.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-md hover:bg-red-50 opacity-0 group-hover:opacity-100 focus:opacity-100" aria-label="Delete file" data-id="element-932">

                      <Trash2 className="h-4 w-4" data-id="element-933" />
                    </button>
                  </div>
                </div>)}

              {files.length === 0 && <div className="px-6 py-12 text-center text-gray-500" data-id="element-934">
                  <p data-id="element-935">No files uploaded yet.</p>
                </div>}
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3 text-sm text-blue-800" data-id="element-936">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0" data-id="element-937" />
            <p data-id="element-938">
              Berry automatically extracts transaction dates, amounts, and
              descriptions. You can review and edit these details in the next
              step.
            </p>
          </div>
        </div>
      </main>

      {/* Bottom Action Bar */}
      <div className="bg-white border-t border-gray-200 py-4 px-4 sm:px-6 lg:px-8 sticky bottom-0 z-10" data-id="element-939">
        <div className="max-w-3xl mx-auto flex justify-between items-center" data-id="element-940">
          <Button variant="ghost" onClick={() => navigate('/connect')} className="text-gray-500 hover:text-gray-900" data-id="element-941">

            Back
          </Button>
          <div className="flex items-center gap-4" data-id="element-942">
            <span className="text-sm text-gray-500 hidden sm:inline" data-id="element-943">
              {files.filter(f => f.status === 'ready').length} of{' '}
              {files.length} files ready
            </span>
            <Button variant="primary" disabled={files.some(f => f.status === 'processing') || files.length === 0} onClick={() => navigate('/reconcile')} className="min-w-[160px]" data-id="element-944">

              Start Reconciling
              <ArrowRight className="ml-2 h-4 w-4" data-id="element-945" />
            </Button>
          </div>
        </div>
      </div>
    </div>;
}